/**
 * Préparation des jets depuis la fiche et format des jets stockés dans le chat de campagne (Realtime DB).
 * Fonctions pures : l'écriture Firebase est dans rollRepository.js.
 */
import {SYSTEMS} from '../utils/gameSystem';
import {ATTRIBUTES_BY_ID, SKILLS_BY_ID, localized} from './data/traits';
import {DISCIPLINES_BY_ID} from './data/disciplines';
import {getHealthMax, getWillpowerMax, isImpaired} from './character';
import {OUTCOMES} from './dice';

export const ROLL_TYPES = {
  POOL: 'pool',
  ROUSE: 'rouse',
  REMORSE: 'remorse',
  FRENZY: 'frenzy',
  // Pouvoir sans jet : seulement ses tests d'exaltation
  POWER: 'power',
  MEND: 'mend',
  FEED: 'feed',
  // Pas un jet : achat d'XP annoncé dans le chat
  XP: 'xp',
};

/** Valeur d'un trait sur la fiche : attribut, compétence ou niveau de discipline. */
export const getTraitValue = (sheet, traitId) => {
  if (ATTRIBUTES_BY_ID[traitId]) return sheet.attributes[traitId] || 0;
  if (SKILLS_BY_ID[traitId]) return sheet.skills[traitId] || 0;
  if (DISCIPLINES_BY_ID[traitId]) return sheet.disciplines[traitId]?.level || 0;
  return 0;
};

export const getTraitLabel = (traitId, lng = 'fr') => localized(
  (ATTRIBUTES_BY_ID[traitId] || SKILLS_BY_ID[traitId] || DISCIPLINES_BY_ID[traitId] || {}).name,
  lng,
) || traitId;

/** Base de la réserve à partir des traits choisis (1 ou 2). */
export const getBaseParts = (sheet, traitIds, lng = 'fr') => traitIds
  .filter(Boolean)
  .map(id => ({label: getTraitLabel(id, lng), value: getTraitValue(sheet, id)}));

/** Libellé court du jet : « Charisme + Persuasion ». */
export const getRollLabel = (traitIds, lng = 'fr') => traitIds.filter(Boolean).map(id => getTraitLabel(id, lng)).join(' + ');

/**
 * Traits pré-remplis pour le jet d'un pouvoir : [attribut, compétence/discipline].
 * Quand la réserve laisse le choix (ex : Charisme ou Manipulation), on prend le meilleur trait du personnage.
 */
export const getPowerTraits = (sheet, pool) => (pool || []).slice(0, 2).map(part => (
  Array.isArray(part)
    ? part.reduce((best, id) => (getTraitValue(sheet, id) > getTraitValue(sheet, best) ? id : best))
    : part
));

/**
 * Malus « Diminué » proposé par défaut : jauge de Santé pleine pour un jet physique,
 * jauge de Volonté pleine pour un jet social ou mental.
 */
export const isRollImpaired = (sheet, firstTraitId) => {
  const physical = ATTRIBUTES_BY_ID[firstTraitId]?.category === 'physical' || SKILLS_BY_ID[firstTraitId]?.category === 'physical';
  return physical
    ? isImpaired(sheet.health, getHealthMax(sheet))
    : isImpaired(sheet.willpower, getWillpowerMax(sheet));
};

/** Spécialités du personnage utilisables avec les traits choisis. */
export const getSpecialtiesFor = (sheet, traitIds) => sheet.specialties.filter(item => traitIds.includes(item.skill));

/**
 * Enregistrement d'un jet pour le chat. Realtime DB ne stocke ni `undefined` ni tableaux vides :
 * tout est normalisé ici, et relu via normalizeRollRecord.
 */
export const createRollRecord = ({id, type, character, userUid, hidden = false, label = '', createdAt = Date.now(), ...details}) => ({
  id,
  system: SYSTEMS.VTM5,
  type,
  createdAt,
  userUid,
  characterId: character.uid,
  characterName: character.name,
  picture: character.picture || null,
  hidden: Boolean(hidden),
  label,
  breakdown: details.breakdown || [],
  regular: details.regular || [],
  hunger: details.hunger || [],
  difficulty: details.difficulty ?? null,
  successes: details.successes ?? null,
  outcome: details.outcome ?? null,
  margin: details.margin ?? null,
  messyIfWin: Boolean(details.messyIfWin),
  bestialIfFail: Boolean(details.bestialIfFail),
  autoFailure: Boolean(details.autoFailure),
  willpowerRerolled: Boolean(details.willpowerRerolled),
  previousRegular: details.previousRegular || [],
  rerolledIndexes: details.rerolledIndexes || [],
  rouse: details.rouse || null,
  // Tests d'exaltation du pouvoir utilisé (en plus d'un éventuel Coup de sang)
  powerId: details.powerId || null,
  powerRouses: details.powerRouses || [],
  remorse: details.remorse || null,
  mend: details.mend || null,
  feed: details.feed || null,
  xp: details.xp || null,
  // Jet demandé par le MJ (rollRequest.js)
  requestId: details.requestId || null,
});

/** Relit un jet venant de Realtime DB (tableaux vides absents, clé Firebase). */
export const normalizeRollRecord = (key, value) => ({
  ...value,
  key,
  breakdown: value.breakdown || [],
  regular: value.regular || [],
  hunger: value.hunger || [],
  previousRegular: value.previousRegular || [],
  rerolledIndexes: value.rerolledIndexes || [],
  rouse: value.rouse ? {...value.rouse, dice: value.rouse.dice || []} : null,
  powerRouses: (value.powerRouses || []).map(check => ({...check, dice: check.dice || []})),
  remorse: value.remorse ? {...value.remorse, dice: value.remorse.dice || []} : null,
});

/** Les jets cachés ne sont visibles que par leur auteur et le MJ. */
export const canSeeRoll = (record, userUid, dmUid) => !record.hidden || record.userUid === userUid || dmUid === userUid;

const WIN_OUTCOMES = [OUTCOMES.WIN, OUTCOMES.CRITICAL_WIN, OUTCOMES.MESSY_CRITICAL];

/**
 * Réussite ou échec d'une entrée du chat, quand on peut le déduire : 'success', 'failure' ou null.
 * Null pour un jet sans difficulté, et pour ce qui n'est pas un test (pouvoir, soin, repas, XP).
 */
export const getRollVerdict = (record) => {
  switch (record.type) {
    case ROLL_TYPES.ROUSE:
      return record.rouse.success ? 'success' : 'failure';
    case ROLL_TYPES.REMORSE:
      return record.remorse.success ? 'success' : 'failure';
    case ROLL_TYPES.POOL:
    case ROLL_TYPES.FRENZY:
      if (record.autoFailure) return 'failure';
      if (!record.outcome) return null;
      return WIN_OUTCOMES.includes(record.outcome) ? 'success' : 'failure';
    default:
      return null;
  }
};

/** Résultats où la Bête se manifeste : effet visible par toute la table. */
export const BEAST_OUTCOMES = [OUTCOMES.MESSY_CRITICAL, OUTCOMES.BESTIAL_FAILURE];

/**
 * Dernier jet dont le résultat vient de devenir un critique sanglant ou un échec bestial
 * (nouveau jet, ou jet relancé à la Volonté). `previousOutcomes` : {clé: résultat} au rendu précédent.
 */
export const findBeastMoment = (previousOutcomes, rolls) => rolls
  .filter(record => BEAST_OUTCOMES.includes(record.outcome) && previousOutcomes[record.key] !== record.outcome)
  .pop() || null;

/** Jets montrés en grand chez le reste de la table : les tests, pas l'intendance (soin, repas, XP). */
export const SHARED_ROLL_TYPES = [ROLL_TYPES.POOL, ROLL_TYPES.FRENZY, ROLL_TYPES.ROUSE, ROLL_TYPES.REMORSE];

/**
 * Dernier jet d'un autre membre de la table qui vient d'arriver, ou d'être relancé à la Volonté.
 * `previous` : {clé: relancé ?} au rendu précédent ; les jets de `userUid` sont ignorés (il les voit déjà).
 */
export const findSharedRoll = (previous, rolls, userUid) => rolls
  .filter(record => SHARED_ROLL_TYPES.includes(record.type) && record.userUid !== userUid)
  .filter(record => !(record.key in previous) || (record.willpowerRerolled && !previous[record.key]))
  .pop() || null;

/**
 * Dernier jet du personnage, s'il peut encore être relancé à la Volonté (jet de réserve avec des dés normaux,
 * pas encore relancé) : le joueur qui a fermé la fenêtre de jet la rouvre depuis le chat.
 */
export const findReopenableRoll = (rolls, characterId) => {
  const last = rolls.filter(record => record.characterId === characterId).pop();
  if (!last || last.type !== ROLL_TYPES.POOL || last.willpowerRerolled || last.regular.length === 0) return null;
  return last;
};

/** Résultat moteur reconstruit depuis un jet du chat, pour la relance de Volonté (willpowerReroll). */
export const rollFromRecord = (record) => ({
  regular: record.regular,
  hunger: record.hunger,
  difficulty: record.difficulty ?? undefined,
  willpowerRerolled: Boolean(record.willpowerRerolled),
});
